import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../shared/ui/Button.js';
import {
  CUSTOMER_CONTROL_RADIUS_SM_CLASS,
  CUSTOMER_MIN_TOUCH_HEIGHT_CLASS,
  CUSTOMER_MIN_TOUCH_TARGET_CLASS,
} from '../../shared/ui/customerTouchTarget.js';
import {
  useDiscoveryShellLocationDisclosure,
  useRegisterDiscoveryShellLocation,
  type DiscoveryManualPlaceSelection,
} from '../../app/DiscoveryShellLocationContext.js';
import { ChevronDown, Icon, MapPin } from 'pi-kiosk-shared/ui/Icon';
import { Card } from '../../shared/ui/Card.js';
import {
  DirectoryResults,
  DirectoryResultsSkeleton,
} from '../../shared/ui/DirectoryResults.js';
import { DirectorySearchField } from '../../shared/ui/DirectorySearchField.js';
import { DirectoryFilterSheet } from '../../shared/ui/DirectoryFilterSheet.js';
import { type FormatDistanceMetersLabels } from '../../shared/ui/directoryListFilter.js';
import {
  DIRECTORY_FAVORITES_LIST_ITEM_CLASS,
  DIRECTORY_PAGE_STACK_CLASS,
} from '../../shared/ui/directoryPickerLayout.js';
import { BrowseBagResults } from './BrowseBagResults.js';
import { BrowseDiscoveryModeStrip } from './BrowseDiscoveryModeStrip.js';
import { mapOfferFavoriteToDiscoveryOffer } from './mapOfferFavoriteToDiscoveryOffer.js';
import { BrowseHomePromoBanner } from './BrowseHomePromoBanner.js';
import {
  BrowseHomeCategoryRail,
  resolveHomeCategoryRailLabel,
} from './BrowseHomeCategoryRail.js';
import {
  mapPendingFavoriteContactsToSalesPointGroups,
  mapSignedInFavoriteContactsToSalesPointGroups,
} from './mapFavoriteContactsToSalesPointGroups.js';
import { BrowseEntityChips } from './BrowseEntityChips.js';
import { BrowseActiveCategoryChip } from './BrowseActiveCategoryChip.js';
import { DiscoveryOfferRowView } from './DiscoveryOfferRowView.js';
import { resolveDiscoveryOfferDisplayImageUrl } from './browseOfferPresentation.js';
import { useStoreDirectoryThumb } from '../../shared/ui/useDirectoryEntityThumb.js';
import {
  directoryThumbFetchPriority,
  directoryThumbLoading,
} from '../../shared/ui/directoryThumbLoading.js';
import {
  DEFAULT_LOGO_CHIP_BACKGROUND_SETTINGS,
  DEFAULT_LOGO_CHIP_RIM_SETTINGS,
} from 'pi-kiosk-shared';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCookieConsent } from '../consent/CookieConsentProvider.js';
import {
  trackDiscoveryResultSelected,
  trackDiscoverySearchPerformed,
} from '../discovery/publicDiscoveryTelemetryClient.js';
import { resolveCustomerAcquisitionSource } from '../discovery/resolveCustomerAcquisitionSource.js';
import { useCustomerFormatters } from '../../shared/hooks/useCustomerFormatters.js';
import { useVisualViewportKeyboardInset } from '../../shared/hooks/useVisualViewportKeyboardInset.js';
import {
  readDiscoveryBrowseSearch,
  clearDiscoveryNearbyContext,
  clearDiscoveryListScroll,
  readDiscoveryMapViewport,
  readDiscoveryNearbyContext,
  readDiscoveryListScroll,
  writeDiscoveryMapViewport,
  writeDiscoveryBrowseSearch,
  writeDiscoveryNearbyContext,
  writeDiscoveryListScroll,
} from '../discovery/discoverySessionRestore.js';
import { isMapAutoPaginateBlockedByCursorRecovery } from './mapAutoPaginateCursorRecoveryGate.js';
import { useDiscoverySearchParams } from '../discovery/useDiscoverySearchParams.js';
import { usePersonalFavoriteSalesPointIds } from '../discovery/usePersonalFavoriteSalesPointIds.js';
import {
  fetchPublicDiscoveryHomeCategories,
  fetchPublicDiscoverySearch,
  fetchPublicPlatformCategoriesTree,
  isPublicDiscoveryCursorValidationError,
  buildPublicDiscoveryHomeBannerStreamUrl,
} from '../discovery/publicDiscoveryApi.js';
import {
  recordCategoryOpen,
  selectVisibleHomeCategories,
} from '../discovery/discoveryHomeCategoryPersonalization.js';
import {
  parseDiscoverySearchParams,
  discoveryFilterIdentityKey,
  DISCOVERY_PARAM_SORT,
  isBrowseTenantDirectorySurface,
} from '../discovery/discoverySearchParams.js';
import { resolveBrowseHomeLayerVisibility } from './browseHomeSixStateVisibility.js';
import { resolveBrowseHomePromoCta } from './resolveBrowseHomePromoCta.js';
import type { DiscoverySort } from '../discovery/discoverySearchParams.js';
import type {
  PublicDiscoveryHomeCategoryItemDTO,
  PublicDiscoveryOfferItemDTO,
  PublicPlatformCategoryItemDTO,
} from '../discovery/publicDiscoveryTypes.js';
import type { PublicCustomerSalesPointDTO } from '../shops/publicSalesPointsApi.js';
import {
  ShopsDirectoryMap,
  shopsDirectoryMapViewportEqual,
  type ShopsDirectoryMapViewportState,
} from '../shops/ShopsDirectoryMap.js';
import { resolveDirectoryShopOpenAction } from '../shops/resolveDirectoryShopOpenAction.js';
import { shouldShowBrowseNearbyShippingOnlyOmitNote } from '../shops/shippingOnlyGeoOmit.js';
import { writeDirectoryGeoAllowedFlag } from '../../shared/ui/directoryGeoAllowed.js';
import { geoPlaceCellKey } from '../../shared/ui/directoryGeoPlace.js';
import { useDirectoryGeoPlaceLabels } from '../../shared/ui/useDirectoryGeoPlaceLabels.js';
import { showToast } from '../../shared/ui/toastStore.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { AgeGateDialog } from '../mor-chrome/AgeGateDialog.js';
import { isSellerLegalCacheSuccessForAgeGate } from '../mor-chrome/ageGateModel.js';
import { fetchSellerLegalAgeGateSnapshot } from '../seller-legal/publicSellerLegalApi.js';
import { addDiscoveryOfferToCart } from './addDiscoveryOfferToCart.js';
import { DiscoveryOfferDetailSheet } from './DiscoveryOfferDetailSheet.js';
import { BrowseRankingDisclosureSheet } from './BrowseRankingDisclosureSheet.js';
import { BrowseNearbySalesPointResults } from './BrowseNearbySalesPointResults.js';
import { BrowseTenantDirectoryResults } from './BrowseTenantDirectoryResults.js';
import { BrowseShopDirectoryResults } from './BrowseShopDirectoryResults.js';
import { BrowseVseSearchSections } from './BrowseVseSearchSections.js';
import { useBrowseShopDirectory } from './useBrowseShopDirectory.js';
import { useBrowseTenantDirectory } from './useBrowseTenantDirectory.js';
import { resolveBrowseListResultsMode } from './browseEntityResultsRouting.js';
import {
  groupDiscoveryOffersBySalesPoint,
  type DiscoveryIntentGroupSortMode,
  type DiscoveryIntentSalesPointGroup,
} from './groupDiscoveryOffersBySalesPoint.js';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_RADIUS_METERS = 5_000;
const FILTER_RADIUS_OPTIONS = [1_000, 5_000, 10_000, 25_000, 50_000] as const;
const GEO_TIMEOUT_MS = 10_000;

type UserPosition = {
  readonly lat: number;
  readonly lng: number;
};

/** H05 — session Nominatim place for cold/warm restore (≠ GPS; ≠ shipping). */
function readInitialManualDiscoveryPlace(): {
  readonly location: UserPosition;
  readonly label: string;
} | null {
  const ctx = readDiscoveryNearbyContext();
  if (ctx?.source !== 'manual' || ctx.manualPlace === undefined) {
    return null;
  }
  return {
    location: { lat: ctx.manualPlace.lat, lng: ctx.manualPlace.lng },
    label: ctx.manualPlace.label,
  };
}

/**
 * Distance sort when nearby geo payload is complete (lat/lng + radius).
 * Spec §16 #6: distance-first when location is known — default to distance
 * for active nearby when URL omits sort (session restore).
 * Explicit URL `sort=relevance` (user chose Relevance) is honored; omitted sort defaults to distance.
 */
function resolveNearbyDiscoverySort(
  hasLocation: boolean,
  sort: DiscoverySort,
  radiusMeters: number | null,
  sortExplicitInUrl: boolean,
): DiscoverySort {
  if (!hasLocation || radiusMeters === null) {
    return sort;
  }
  if (sort === 'distance') {
    return 'distance';
  }
  if (sort === 'relevance' && sortExplicitInUrl) {
    return 'relevance';
  }
  return 'distance';
}

/** Geo query shape: never lat/lng without radius; never silent geo after filter reset. */
function resolveDiscoveryGeoPayload(
  location: UserPosition | null,
  radiusMeters: number | null,
  sort: DiscoverySort,
): {
  readonly nearbyActive: boolean;
  readonly lat: number | null;
  readonly lng: number | null;
  readonly radius: number | null;
} {
  if (location === null) {
    return { nearbyActive: false, lat: null, lng: null, radius: null };
  }
  if (radiusMeters !== null) {
    return {
      nearbyActive: true,
      lat: location.lat,
      lng: location.lng,
      radius: radiusMeters,
    };
  }
  // Nearby CTA may commit sort=distance before radius URL merge; fill default only then.
  // clearFilters leaves sort=relevance + null radius — do not re-attach DEFAULT (silent geo).
  if (sort === 'distance') {
    return {
      nearbyActive: true,
      lat: location.lat,
      lng: location.lng,
      radius: DEFAULT_RADIUS_METERS,
    };
  }
  return { nearbyActive: false, lat: null, lng: null, radius: null };
}

function acquireBrowserPosition(
  onSuccess: (position: UserPosition) => void,
  onError: (denied: boolean) => void,
): () => void {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.geolocation?.getCurrentPosition !== 'function'
  ) {
    onError(false);
    return () => undefined;
  }

  let cancelled = false;
  navigator.geolocation.getCurrentPosition(
    (position) => {
      if (cancelled) {
        return;
      }
      onSuccess({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
    },
    (error) => {
      if (cancelled) {
        return;
      }
      onError(error.code === error.PERMISSION_DENIED);
    },
    { timeout: GEO_TIMEOUT_MS, enableHighAccuracy: false },
  );

  return () => {
    cancelled = true;
  };
}

type FlattenedCategoryOption = {
  readonly category: PublicPlatformCategoryItemDTO;
  readonly depth: number;
};

/** Depth-first walk of nested `children` for the all-categories sheet (L1/L2/L3). */
function flattenCategoryTree(
  items: readonly PublicPlatformCategoryItemDTO[],
): FlattenedCategoryOption[] {
  const out: FlattenedCategoryOption[] = [];
  const walk = (nodes: readonly PublicPlatformCategoryItemDTO[], depth: number): void => {
    for (const category of nodes) {
      out.push({ category, depth });
      const children = category.children;
      if (children !== undefined && children.length > 0) {
        walk(children, depth + 1);
      }
    }
  };
  walk(items, 0);
  return out;
}

/** G4 — chip/taxonomy labels: localized name → categoryFallback; never raw slug/tech id. */
function categoryLabel(
  category: Pick<PublicDiscoveryHomeCategoryItemDTO, 'name' | 'nameLocales'> & {
    readonly slug: string;
  },
  locale: string,
  fallbackLabel: string,
): string {
  return resolveHomeCategoryRailLabel(category, locale, fallbackLabel);
}

/** Variant suffix for React keys / testids / open handlers (G11). */
function offerVariantSuffix(variantId: number | null): string {
  return variantId === null ? 'base' : String(variantId);
}

/** Stable offer identity: productId + variantId (never productId alone). */
function offerRowIdentity(offer: PublicDiscoveryOfferItemDTO): string {
  return `${offer.productContext.productId}-${offerVariantSuffix(offer.productContext.variantId)}`;
}

/**
 * One Leaflet pin per sales point (map constraint). Pin identity is SP-level —
 * `location`/`name` are the sales point, not a silently winning offer title.
 * Multi-offer choice is explicit via the map offer picker (G8).
 */
function toMapItems(items: readonly PublicDiscoveryOfferItemDTO[]): PublicCustomerSalesPointDTO[] {
  const bySalesPoint = new Map<number, PublicCustomerSalesPointDTO>();
  for (const item of items) {
    const lat = item.salesPointContext.lat;
    const lng = item.salesPointContext.lng;
    if (lat === null || lng === null) {
      continue;
    }
    const salesPointId = item.salesPointContext.salesPointId;
    if (bySalesPoint.has(salesPointId)) {
      continue;
    }
    bySalesPoint.set(salesPointId, {
      salesPointId,
      code: item.salesPointContext.code,
      name: item.salesPointContext.name,
      location: item.salesPointContext.name,
      lat,
      lng,
      // Radius/map pins are STOREFRONT-only (ONLINE_ONLY never reaches discovery geo).
      customerPresence: 'STOREFRONT',
      favoriteCount: 0,
      tenantId: item.tenantContext.tenantId,
      tenantCode: item.tenantContext.tenantCode,
      tenantName: item.tenantContext.tenantName,
      customerShopUrl: item.salesPointContext.customerShopUrl,
      imageUrl: null,
      imageUrlDark: null,
      tenantLogoUrl: null,
      tenantLogoUrlDark: null,
      // Mapa selection-card path — pin uses selection card, not store-photo corner chip.
      showTenantLogoOnStorePhoto: false,
      ...DEFAULT_LOGO_CHIP_RIM_SETTINGS,
      ...DEFAULT_LOGO_CHIP_BACKGROUND_SETTINGS,
    } satisfies PublicCustomerSalesPointDTO);
  }
  return [...bySalesPoint.values()];
}

/** Offers at one SP for the map picker — deterministic list order only (not navigate winner). */
function offersForSalesPoint(
  items: readonly PublicDiscoveryOfferItemDTO[],
  salesPointId: number,
): PublicDiscoveryOfferItemDTO[] {
  const rank = (offer: PublicDiscoveryOfferItemDTO): string => {
    const variantRank =
      offer.productContext.variantId === null
        ? 'z'
        : String(offer.productContext.variantId).padStart(12, '0');
    return `${String(offer.productContext.productId).padStart(12, '0')}:${variantRank}`;
  };
  return items
    .filter(
      (item) =>
        item.salesPointContext.salesPointId === salesPointId &&
        item.salesPointContext.lat !== null &&
        item.salesPointContext.lng !== null &&
        // GoPay MoR — HARD BLOCK empty Product.description in directory.
        typeof item.description === 'string' &&
        item.description.trim().length > 0,
    )
    .slice()
    .sort((a, b) => {
      const aKey = rank(a);
      const bKey = rank(b);
      if (aKey < bKey) {
        return -1;
      }
      if (aKey > bKey) {
        return 1;
      }
      return 0;
    });
}

function DiscoveryOfferRow(props: {
  readonly offer: PublicDiscoveryOfferItemDTO;
  readonly distanceFormatter: FormatDistanceMetersLabels;
  readonly formatPrice: (amountMajor: number) => string;
  readonly onAdd: (offer: PublicDiscoveryOfferItemDTO) => void;
  readonly onDetail: (offer: PublicDiscoveryOfferItemDTO) => void;
  readonly onGoToShop: (offer: PublicDiscoveryOfferItemDTO) => void;
  readonly grouped?: boolean;
}): JSX.Element {
  const { offer, distanceFormatter, formatPrice, onAdd, onDetail, grouped = false } = props;
  const { t } = useTranslation('customer');
  const rowIdentity = offerRowIdentity(offer);
  const addDisabled =
    offer.price === null ||
    typeof offer.description !== 'string' ||
    offer.description.trim().length === 0;
  const displayImageUrl = resolveDiscoveryOfferDisplayImageUrl(offer);
  const { thumbProps } = useStoreDirectoryThumb({
    imageUrl: displayImageUrl,
    imageUrlDark: null,
    tenantLogoUrl: null,
    tenantLogoUrlDark: null,
    monogram: {
      id: offer.productContext.productId,
      displayName: offer.title,
      code: offer.salesPointContext.code,
    },
    showTenantChip: false,
    size: 'md',
    loading: directoryThumbLoading(0),
    fetchPriority: directoryThumbFetchPriority(0),
    testId: `customer-browse-media-${rowIdentity}`,
  });

  return (
    <li
      className={DIRECTORY_FAVORITES_LIST_ITEM_CLASS}
      data-testid={`customer-browse-result-${rowIdentity}`}
    >
      <DiscoveryOfferRowView
        offer={offer}
        distanceFormatter={distanceFormatter}
        thumb={thumbProps}
        density="compact"
        layout="inline"
        grouped={grouped}
        titleTestId={`customer-browse-title-${rowIdentity}`}
        testId={`customer-browse-row-${rowIdentity}`}
        trailing={
          <div
            className="flex shrink-0 items-center gap-1"
            data-testid={`customer-browse-actions-${rowIdentity}`}
          >
            {offer.price !== null ? (
              <span
                className="mr-1 text-[length:var(--vcl-type-body-sm-size)] font-semibold tabular-nums text-[var(--color-on-surface)]"
                data-testid={`customer-browse-price-${rowIdentity}`}
              >
                {formatPrice(offer.price)}
              </span>
            ) : (
              <span
                className="mr-1 text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]"
                data-testid="customer-browse-price-unavailable"
              >
                {t('browse.discovery.priceUnavailable')}
              </span>
            )}
            {/*
              HUX-10 / D21 / §3.2.15 — exactly ONE primary quick-add on the list row.
              Green + cart add is the sole primary; Taška★ bag-in lives on DiscoveryOfferDetailSheet
              (demoted off the row so bag-in + green + are never co-equal primaries).
            */}
            <button
              type="button"
              className={[
                'inline-flex items-center justify-center rounded-full',
                'bg-[var(--color-success)] text-[var(--color-on-success,var(--color-primary-foreground))]',
                'hover:opacity-90',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
                'focus-visible:outline-[var(--color-focus-ring)]',
                'disabled:cursor-not-allowed disabled:opacity-50',
                CUSTOMER_MIN_TOUCH_TARGET_CLASS,
              ].join(' ')}
              disabled={addDisabled}
              aria-label={t('browse.discovery.addAria', { name: offer.title })}
              data-testid={`customer-browse-add-${rowIdentity}`}
              data-primary-quick-add="1"
              onClick={() => {
                onAdd(offer);
              }}
            >
              <span aria-hidden="true" className="text-lg font-semibold leading-none">
                +
              </span>
            </button>
            <button
              type="button"
              className={[
                'inline-flex items-center justify-center rounded-full',
                'text-[var(--color-on-surface-muted)] hover:bg-[var(--color-surface-muted)]',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
                'focus-visible:outline-[var(--color-focus-ring)]',
                CUSTOMER_MIN_TOUCH_TARGET_CLASS,
              ].join(' ')}
              aria-label={t('browse.discovery.detailAria', { name: offer.title })}
              data-testid={`customer-browse-detail-${rowIdentity}`}
              onClick={() => {
                onDetail(offer);
              }}
            >
              <span className="text-xs font-medium">{t('browse.discovery.detail')}</span>
            </button>
          </div>
        }
      />
    </li>
  );
}

function resolveBrowseEmptyStateKind(input: {
  readonly favoritesView: boolean;
  readonly offersEmpty: boolean;
  readonly hasLocationContext: boolean;
  readonly radiusMeters: number | null;
}): 'default' | 'nearby' | 'favorites' {
  if (input.favoritesView) {
    return 'favorites';
  }
  if (input.offersEmpty && input.hasLocationContext && input.radiusMeters !== null) {
    return 'nearby';
  }
  return 'default';
}

function browseEmptyStateTitleKey(
  kind: 'default' | 'nearby' | 'favorites',
): 'browse.discovery.emptyTitle' | 'browse.discovery.emptyNearbyTitle' | 'browse.discovery.favoritesEmptyTitle' {
  if (kind === 'favorites') {
    return 'browse.discovery.favoritesEmptyTitle';
  }
  if (kind === 'nearby') {
    return 'browse.discovery.emptyNearbyTitle';
  }
  return 'browse.discovery.emptyTitle';
}

function browseEmptyStateBodyKey(
  kind: 'default' | 'nearby' | 'favorites',
): 'browse.discovery.emptyBody' | 'browse.discovery.emptyNearbyBody' | 'browse.discovery.favoritesEmptyBody' {
  if (kind === 'favorites') {
    return 'browse.discovery.favoritesEmptyBody';
  }
  if (kind === 'nearby') {
    return 'browse.discovery.emptyNearbyBody';
  }
  return 'browse.discovery.emptyBody';
}

export function BrowsePlaceholderPage(): JSX.Element {
  const { t, i18n } = useTranslation('customer');
  const { formatPrice } = useCustomerFormatters();
  const keyboardInsetPx = useVisualViewportKeyboardInset();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { apiBaseUrl, consentIngestGateEnabled } = useRuntimeConfig();
  const cookieConsent = useCookieConsent();
  const {
    params,
    searchParams,
    replaceParams,
    pushParams,
    setQ,
    setPlatformCategory,
    clearCategory,
    setRadiusMeters,
    setSort,
    setView,
    setEntity,
    setListKind,
    clearFilters,
  } = useDiscoverySearchParams();
  const {
    favoriteSalesPointIds,
    isLoading: favoritesLoading,
    signedInFavoriteContacts,
    guestPendingFavorites,
    signedIn: favoritesSignedIn,
  } = usePersonalFavoriteSalesPointIds();
  /**
   * G4: draft is URL-derived (`params.q`) unless the user is typing (`draftOverride`).
   * POP/URL restore updates `params.q` → input shows restored q with no setState-in-effect.
   */
  const [draftOverride, setDraftOverride] = useState<string | null>(null);
  const draftQuery = draftOverride ?? params.q;
  /** GPS vs Nominatim manual place — discovery location only (≠ shipping). */
  const [manualBootstrap] = useState(readInitialManualDiscoveryPlace);
  const [location, setLocation] = useState<UserPosition | null>(
    () => manualBootstrap?.location ?? null,
  );
  const [locationSource, setLocationSource] = useState<'gps' | 'manual' | null>(
    () => (manualBootstrap !== null ? 'manual' : null),
  );
  const [manualLocationLabel, setManualLocationLabel] = useState<string | null>(
    () => manualBootstrap?.label ?? null,
  );
  const [geoPending, setGeoPending] = useState(false);
  const [geoErrorKey, setGeoErrorKey] = useState<'denied' | 'unavailable' | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  /** W7 / H08 — ranking disclosure („Jak řadíme výsledky“). */
  const [rankingDisclosureOpen, setRankingDisclosureOpen] = useState(false);
  /** Offer detail sheet (list Detail CTA) — null when closed. */
  const [detailOffer, setDetailOffer] = useState<PublicDiscoveryOfferItemDTO | null>(null);
  const [detailFromGrouped, setDetailFromGrouped] = useState(false);
  /** GoPay MoR G5 — 18+ dialog tenant + offer awaiting ack before browse add. */
  const [ageGateDialogTenant, setAgeGateDialogTenant] = useState<string | null>(null);
  const [pendingAgeGateOffer, setPendingAgeGateOffer] = useState<PublicDiscoveryOfferItemDTO | null>(
    null,
  );
  const [ageGateDialogReviewRisk, setAgeGateDialogReviewRisk] = useState<string | null>(null);
  const api = useApiClient();
  /** Map pin highlight / multi-offer picker SP (null while single-offer navigates). */
  const [selectedSalesPointId, setSelectedSalesPointId] = useState<number | null>(null);
  /** Explicit multi-offer chooser for one SP (G8) — not silent collapse. */
  const [mapOfferPickerSalesPointId, setMapOfferPickerSalesPointId] = useState<
    number | null
  >(null);
  const cursorRecoveryAttemptedKeyRef = useRef<string | null>(null);
  const activeSearchRequestIdRef = useRef<string | null>(null);
  const lastSearchTelemetryKeyRef = useRef<string | null>(null);
  const prevDiscoveryFilterIdentityRef = useRef<string>(
    discoveryFilterIdentityKey(params),
  );
  const [mapViewport, setMapViewport] =
    useState<ShopsDirectoryMapViewportState | null>(() =>
      readDiscoveryMapViewport(),
    );
  /** G2 — React-applied list restore Y (not native history). Omitted until browse effect runs. */
  const [appliedListScrollRestoreY, setAppliedListScrollRestoreY] = useState<number | null>(
    null,
  );
  const queryClient = useQueryClient();
  const didRestorePopSearchRef = useRef(false);
  const { openLocationDisclosure } = useDiscoveryShellLocationDisclosure();

  useEffect(() => {
    if (didRestorePopSearchRef.current || navigationType !== 'POP') {
      return;
    }
    const hasExplicitDiscoverySearch =
      searchParams.has('q') ||
      searchParams.has('platformCategory') ||
      searchParams.has('radius') ||
      searchParams.has('sort') ||
      searchParams.has('view');
    if (hasExplicitDiscoverySearch) {
      didRestorePopSearchRef.current = true;
      return;
    }
    const restored = readDiscoveryBrowseSearch();
    if (restored === null) {
      didRestorePopSearchRef.current = true;
      return;
    }
    didRestorePopSearchRef.current = true;
    const restoredParams = new URLSearchParams(restored.search);
    const restoredState = parseDiscoverySearchParams(restoredParams);
    // draftOverride stays null on cold POP → draftQuery tracks restored params.q (G4).
    replaceParams(restoredState);
  }, [navigationType, replaceParams, searchParams]);

  useEffect(() => {
    if (draftOverride === null || draftOverride === params.q) {
      return;
    }
    const handle = window.setTimeout(() => {
      setQ(draftOverride);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draftOverride, params.q, setQ]);

  useEffect(() => {
    const next = searchParams.toString();
    writeDiscoveryBrowseSearch({ search: next.length > 0 ? `?${next}` : '?' });
  }, [searchParams]);

  // Exact GPS stays in React state only (§6). Manual Nominatim centroid may restore from session (H05).
  const resolvedLocation = location;

  useEffect(() => {
    if (location === null) {
      return;
    }
    if (locationSource === 'manual' && manualLocationLabel !== null) {
      writeDiscoveryNearbyContext({
        active: true,
        source: 'manual',
        manualPlace: {
          lat: location.lat,
          lng: location.lng,
          label: manualLocationLabel,
        },
      });
      return;
    }
    writeDiscoveryNearbyContext({ active: true, source: 'gps' });
    writeDirectoryGeoAllowedFlag(true);
  }, [location, locationSource, manualLocationLabel]);

  const hasDiscoveryQuery = params.q.trim().length > 0;
  /** Screen 3 — empty-q Seznam Tenanti; never nearby-SP / offer fallthrough. */
  const isTenantDirectorySurface = isBrowseTenantDirectorySurface(params);
  const shopDirectory = useBrowseShopDirectory(params);
  const tenantDirectory = useBrowseTenantDirectory(params);

  const hasCategoryFilter = params.platformCategory !== null;
  const hasNearbyUrlIntent =
    params.sort === 'distance' || params.radiusMeters !== null;
  const hasNearbySessionIntent = readDiscoveryNearbyContext() !== null;
  const hasLocationContext = resolvedLocation !== null;
  /**
   * Shell / data-nearby-active (G5/G13): stable coarse nearby intent — live GPS OR
   * session+URL warm remount — without geoPending oscillation on silent reacquire.
   */
  const hasNearbyShellIntent =
    hasLocationContext || (hasNearbyUrlIntent && hasNearbySessionIntent);

  const userGeoPlaceItems = useMemo(
    () =>
      resolvedLocation !== null
        ? [{ lat: resolvedLocation.lat, lng: resolvedLocation.lng }]
        : [],
    [resolvedLocation],
  );
  const userPlaceLabels = useDirectoryGeoPlaceLabels(
    userGeoPlaceItems,
    resolvedLocation !== null && hasNearbyShellIntent,
    { acceptLanguage: i18n.language },
  );
  const userLocationLabel = useMemo((): string | null => {
    if (resolvedLocation === null) {
      return null;
    }
    if (
      manualLocationLabel !== null &&
      manualLocationLabel.trim().length > 0
    ) {
      return manualLocationLabel.trim();
    }
    const key = geoPlaceCellKey(resolvedLocation.lat, resolvedLocation.lng);
    return userPlaceLabels.get(key) ?? null;
  }, [manualLocationLabel, resolvedLocation, userPlaceLabels]);

  // Nearby URL without live GPS is inactive for payloads (no lat/lng) but not landing.
  /** Neutral browse surface: search + categories only (no query/filter/nearby intent). */
  const isNeutralDiscoverySurface =
    !hasDiscoveryQuery &&
    !hasCategoryFilter &&
    !hasLocationContext &&
    !hasNearbyUrlIntent;

  // Warm-reacquire on remount when coarse intent + radius/sort URL remain (G13/G1).
  // Defer acquire to a macrotask so Strict Mode cleanup does not cancel a same-tick
  // getCurrentPosition before the geo mock timeout runs. Silent reacquire — do not
  // toggle geoPending (user-initiated requestNearby owns pending disclosure state).
  // H05: never GPS-surprise when session holds a Nominatim manual place.
  useEffect(() => {
    if (!hasNearbyUrlIntent || location !== null || !hasNearbySessionIntent) {
      return;
    }
    if (readDiscoveryNearbyContext()?.source === 'manual') {
      return;
    }
    let cancelled = false;
    let release: (() => void) | undefined;
    const startHandle = window.setTimeout(() => {
      if (cancelled) {
        return;
      }
      release = acquireBrowserPosition(
        (position) => {
          if (cancelled) {
            return;
          }
          setLocation(position);
          setGeoErrorKey(null);
        },
        (denied) => {
          if (cancelled) {
            return;
          }
          setGeoErrorKey(denied ? 'denied' : 'unavailable');
          if (denied) {
            writeDirectoryGeoAllowedFlag(false);
          }
        },
      );
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(startHandle);
      release?.();
    };
  }, [hasNearbySessionIntent, hasNearbyUrlIntent, location]);

  // G2: disable native history scroll so POP restore is only React scrollTo({ top, behavior: 'auto' }).
  useEffect(() => {
    if (!('scrollRestoration' in window.history)) {
      return;
    }
    window.history.scrollRestoration = 'manual';
  }, []);

  useEffect(() => {
    if (isNeutralDiscoverySurface) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      return;
    }
    const restored = readDiscoveryListScroll();
    if (restored === null) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      const top = restored.scrollY;
      // Unique signature vs native history / two-arg window.scrollTo(x, y) / Element.scrollTo.
      window.scrollTo({ top, behavior: 'auto' });
      // Also pin documentElement so live scrollY/scrollTop is measurable after POP.
      document.documentElement.scrollTop = top;
      setAppliedListScrollRestoreY(top);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isNeutralDiscoverySurface]);

  useEffect(() => {
    return () => {
      writeDiscoveryListScroll({ scrollY: window.scrollY });
    };
  }, []);

  const categoriesQuery = useQuery({
    queryKey: customerQueryKeys.publicDiscoveryPlatformCategoriesTree(
      apiBaseUrl,
      i18n.resolvedLanguage ?? i18n.language ?? 'cs',
    ),
    queryFn: ({ signal }) =>
      fetchPublicPlatformCategoriesTree(apiBaseUrl, {
        locale: i18n.resolvedLanguage ?? i18n.language ?? 'cs',
        signal,
      }),
    staleTime: 30_000,
  });

  const homeCategoriesQuery = useQuery({
    queryKey: customerQueryKeys.publicDiscoveryHomeCategories(
      apiBaseUrl,
      i18n.resolvedLanguage ?? i18n.language ?? 'cs',
    ),
    queryFn: ({ signal }) =>
      fetchPublicDiscoveryHomeCategories(apiBaseUrl, {
        locale: i18n.resolvedLanguage ?? i18n.language ?? 'cs',
        signal,
      }),
    staleTime: 30_000,
  });

  const homeCategorySlotCount = homeCategoriesQuery.data?.slotCount ?? 6;

  const visibleHomeCategories = useMemo((): PublicDiscoveryHomeCategoryItemDTO[] => {
    const items = homeCategoriesQuery.data?.items ?? [];
    const slotCount: 3 | 6 = homeCategorySlotCount === 3 ? 3 : 6;
    return selectVisibleHomeCategories(items, slotCount);
  }, [homeCategoriesQuery.data?.items, homeCategorySlotCount]);

  const homeCategoriesLoading =
    homeCategoriesQuery.isPending || homeCategoriesQuery.isLoading;
  const homeCategoriesEmpty =
    !homeCategoriesLoading &&
    !homeCategoriesQuery.isError &&
    homeCategoriesQuery.data !== undefined &&
    visibleHomeCategories.length === 0;

  /**
   * §3.2.14 / D21 / D22 — six-state visibility owned here (parent), not in rail/promo children.
   * Rail: default Seznam Screen-1 including active category (D15 affordance).
   * Promo: Screen-1 only when platformCategory null (D17). GPS/nearby must not hide.
   * Incomplete promo → BrowseHomePromoBanner collapses (FR-14).
   */
  const homeVisibility = resolveBrowseHomeLayerVisibility(params);
  const showHomePromoSurface = homeVisibility.showPromo;
  const showHomeCategoriesSurface = homeVisibility.showCategoryRail;
  const homePromoBanner = showHomePromoSurface
    ? (homeCategoriesQuery.data?.banner ?? null)
    : null;
  const homePromoThumbKey = homePromoBanner?.bannerThumbnailKey?.trim() ?? '';
  const homePromoImageUrl =
    homePromoBanner !== null && homePromoBanner.bannerStorageKey.trim().length > 0
      ? buildPublicDiscoveryHomeBannerStreamUrl(
          apiBaseUrl,
          homePromoThumbKey.length > 0 ? 'thumbnail' : 'full',
        )
      : null;

  /** FR-14 — null when target incomplete / unresolvable (banner collapses; no no-op chrome). */
  const homePromoCtaAction =
    homePromoBanner === null
      ? null
      : resolveBrowseHomePromoCta({
          targetType: homePromoBanner.targetType,
          targetRef: homePromoBanner.targetRef,
        });

  const homePromoCtaLabel = homePromoBanner?.ctaLabel?.trim() ?? '';
  const homePromoTitle = homePromoBanner?.title?.trim() ?? '';
  const homePromoActionable =
    homePromoBanner !== null &&
    homePromoImageUrl !== null &&
    homePromoTitle.length > 0 &&
    homePromoCtaLabel.length > 0 &&
    homePromoCtaAction !== null;

  const handleHomePromoCta = useCallback((): void => {
    if (homePromoCtaAction === null) {
      return;
    }
    if (homePromoCtaAction.kind === 'external') {
      window.open(homePromoCtaAction.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (homePromoCtaAction.kind === 'browseRoot') {
      setView('list');
      navigate('/browse');
      return;
    }
    navigate(homePromoCtaAction.url);
  }, [homePromoCtaAction, navigate, setView]);

  const renderHomeCategoriesArea = (): JSX.Element => {
    if (homeCategoriesQuery.isError) {
      const categoriesErrorBody =
        typeof navigator !== 'undefined' && navigator.onLine === false
          ? t('browse.discovery.offlineBody')
          : t('browse.discovery.categoriesErrorBody');
      return (
        <Card padded data-testid="customer-browse-categories-error">
          <div className="space-y-2">
            <h3 className="text-[length:var(--vcl-type-h3-size)] font-[number:var(--vcl-type-h3-weight)] text-[var(--color-on-surface)]">
              {t('browse.discovery.categoriesErrorTitle')}
            </h3>
            <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">{categoriesErrorBody}</p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                intent="primary"
                size="md"
                onClick={() => {
                  void homeCategoriesQuery.refetch();
                }}
                data-testid="customer-browse-categories-retry"
              >
                {t('shared.retry')}
              </Button>
            </div>
          </div>
        </Card>
      );
    }

    if (homeCategoriesLoading) {
      return (
        <div
          className="flex w-full min-w-0 gap-1 overflow-x-hidden overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="customer-browse-categories-loading"
          aria-busy="true"
        >
          {['sk-cat-1', 'sk-cat-2', 'sk-cat-3', 'sk-cat-4', 'sk-cat-5', 'sk-cat-6'].map((key) => (
            <div
              key={key}
              className="h-[4.5rem] min-h-11 min-w-11 max-w-[3.75rem] flex-1 basis-0 animate-pulse rounded-lg bg-[var(--color-surface-muted)]"
            />
          ))}
        </div>
      );
    }

    if (homeCategoriesEmpty) {
      return (
        <Card padded data-testid="customer-browse-categories-empty">
          <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
            {t('browse.discovery.categoriesEmpty')}
          </p>
        </Card>
      );
    }

    const slotCount: 3 | 6 = homeCategorySlotCount === 3 ? 3 : 6;
    return (
      <BrowseHomeCategoryRail
        categories={visibleHomeCategories}
        slotCount={slotCount}
        activeCategoryKey={params.platformCategory}
        locale={i18n.resolvedLanguage ?? i18n.language ?? 'cs'}
        allLabel={t('browse.discovery.categoryRailAll')}
        moreLabel={t('browse.discovery.moreCategories')}
        comingSoonLabel={t('browse.discovery.comingSoon')}
        fallbackLabel={t('browse.discovery.categoryFallback')}
        onSelectAll={() => {
          clearCategory();
        }}
        onSelectCategory={(slug) => {
          recordCategoryOpen(slug);
          setPlatformCategory(slug);
        }}
        onClearCategory={clearCategory}
        onOpenMore={() => {
          dismissConflictingOverlays();
          setCategoryPickerOpen(true);
        }}
      />
    );
  };

  const discoveryGeo = resolveDiscoveryGeoPayload(
    resolvedLocation,
    params.radiusMeters,
    params.sort,
  );

  const sortExplicitInUrl = searchParams.has(DISCOVERY_PARAM_SORT);

  const discoveryQueryKey = customerQueryKeys.publicDiscoverySearch(apiBaseUrl, {
    q: params.q,
    platformCategory: params.platformCategory,
    radiusMeters: discoveryGeo.radius,
    sort: resolveNearbyDiscoverySort(
      discoveryGeo.nearbyActive,
      params.sort,
      discoveryGeo.radius,
      sortExplicitInUrl,
    ),
    hasLocation: discoveryGeo.nearbyActive,
    locationIdentity:
      discoveryGeo.nearbyActive && resolvedLocation !== null
        ? `${resolvedLocation.lat.toFixed(5)}:${resolvedLocation.lng.toFixed(5)}`
        : null,
    locale: i18n.resolvedLanguage ?? i18n.language ?? 'cs',
  });

  const recoveryKey = useMemo(() => JSON.stringify(discoveryQueryKey), [discoveryQueryKey]);

  const discoveryQuery = useInfiniteQuery({
    queryKey: discoveryQueryKey,
    queryFn: ({ pageParam, signal }) => {
      const geo = resolveDiscoveryGeoPayload(
        resolvedLocation,
        params.radiusMeters,
        params.sort,
      );
      const effectiveSort = resolveNearbyDiscoverySort(
        geo.nearbyActive,
        params.sort,
        geo.radius,
        sortExplicitInUrl,
      );
      return fetchPublicDiscoverySearch(apiBaseUrl, {
        q: params.q,
        platformCategory: params.platformCategory,
        lat: geo.lat,
        lng: geo.lng,
        radius: geo.radius,
        sort: effectiveSort,
        cursor: pageParam,
        locale: i18n.resolvedLanguage ?? i18n.language ?? 'cs',
        signal,
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled:
      !isTenantDirectorySurface &&
      params.entity !== 'shop' &&
      !(params.entity === 'tenant'),
    // Discovery offer search must NOT inherit directory poll/refetchInterval —
    // that starved fetchNextPage (cursor never sent; load-more stuck).
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!discoveryQuery.isError) {
      // Clear one-shot guard after a successful settled load so a later stale
      // cursor under the same recoveryKey can recover again (G8).
      if (
        discoveryQuery.isSuccess &&
        !discoveryQuery.isFetching &&
        !discoveryQuery.isFetchingNextPage
      ) {
        cursorRecoveryAttemptedKeyRef.current = null;
      }
      return;
    }
    if (!isPublicDiscoveryCursorValidationError(discoveryQuery.error)) {
      return;
    }
    if (cursorRecoveryAttemptedKeyRef.current === recoveryKey) {
      return;
    }

    cursorRecoveryAttemptedKeyRef.current = recoveryKey;
    // G2: defer resetQueries so the map auto-paginate effect can observe the
    // unique window (isError + hasNextPage + stale nextCursor still on the
    // page stack). Immediate reset would wipe pageParams first — ungated
    // fetchNextPage would then use initialPageParam null, not the stale
    // cursor. Unique-window proof is the twin tests (not these comments):
    // ungated bypass → staleCursorCalls === 2; gated path → staleCursorCalls === 1.
    // resetQueries still clears infinite pageParams/pages so recovery starts
    // at page 1 (null cursor). removeQueries alone can leave stale stack.
    queueMicrotask(() => {
      void queryClient.resetQueries({
        queryKey: discoveryQueryKey,
        exact: true,
      });
    });
  }, [
    recoveryKey,
    discoveryQuery.error,
    discoveryQuery.isError,
    discoveryQuery.isSuccess,
    discoveryQuery.isFetching,
    discoveryQuery.isFetchingNextPage,
    discoveryQueryKey,
    queryClient,
  ]);

  const offers = useMemo(
    () => discoveryQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [discoveryQuery.data?.pages],
  );

  const analyticsConsentGranted = cookieConsent.decision?.analytics === true;

  useEffect(() => {
    if (
      isTenantDirectorySurface ||
      params.entity === 'shop' ||
      params.entity === 'tenant'
    ) {
      return;
    }
    if (
      !discoveryQuery.isSuccess ||
      discoveryQuery.isFetching ||
      discoveryQuery.isFetchingNextPage
    ) {
      return;
    }
    if (lastSearchTelemetryKeyRef.current === recoveryKey) {
      return;
    }
    lastSearchTelemetryKeyRef.current = recoveryKey;
    const searchRequestId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `search-${Date.now()}`;
    activeSearchRequestIdRef.current = searchRequestId;
    const urlParams = new URLSearchParams(window.location.search);
    const source = resolveCustomerAcquisitionSource({
      urlSearchParams: urlParams,
      documentReferrer: document.referrer,
    });
    trackDiscoverySearchPerformed({
      apiBaseUrl,
      searchRequestId,
      resultCount: offers.length,
      source,
      entity: params.entity ?? null,
      platformCategoryId: params.platformCategory ?? null,
      consentIngestGateEnabled,
      analyticsConsentGranted,
    });
  }, [
    analyticsConsentGranted,
    apiBaseUrl,
    consentIngestGateEnabled,
    discoveryQuery.isFetching,
    discoveryQuery.isFetchingNextPage,
    discoveryQuery.isSuccess,
    isTenantDirectorySurface,
    offers.length,
    params.entity,
    params.platformCategory,
    recoveryKey,
  ]);

  const emitDiscoveryOfferSelection = useCallback(
    (
      offer: PublicDiscoveryOfferItemDTO,
      resultKind: 'open' | 'add' | 'shop',
    ): void => {
      const searchRequestId = activeSearchRequestIdRef.current;
      if (searchRequestId === null) {
        return;
      }
      const urlParams = new URLSearchParams(window.location.search);
      const source = resolveCustomerAcquisitionSource({
        urlSearchParams: urlParams,
        documentReferrer: document.referrer,
      });
      trackDiscoveryResultSelected({
        apiBaseUrl,
        searchRequestId,
        resultKind,
        targetTenantCode: offer.tenantContext.tenantCode,
        targetTenantId: offer.tenantContext.tenantId,
        targetProductId: offer.productContext.productId,
        targetSalesPointId: offer.salesPointContext.salesPointId,
        source,
        consentIngestGateEnabled,
        analyticsConsentGranted,
      });
    },
    [
      analyticsConsentGranted,
      apiBaseUrl,
      consentIngestGateEnabled,
    ],
  );

  const visibleOffers = useMemo(() => offers, [offers]);
  /** Sanitize always nulls intent* after entity remap — no client-side intent filter. */
  const offersForResultsView = visibleOffers;

  const intentGroupSortMode = useMemo((): DiscoveryIntentGroupSortMode => {
    const geo = resolveDiscoveryGeoPayload(
      resolvedLocation,
      params.radiusMeters,
      params.sort,
    );
    const effectiveSort = resolveNearbyDiscoverySort(
      geo.nearbyActive,
      params.sort,
      geo.radius,
      sortExplicitInUrl,
    );
    return effectiveSort === 'distance' ? 'distance' : 'relevance';
  }, [params.radiusMeters, params.sort, resolvedLocation, sortExplicitInUrl]);

  /** Nearby browse (no q): one section per sales point, distance-ordered (mockup list). */
  const nearbySalesPointGroups = useMemo(() => {
    // Wave 2.8 / G2 — listKind=tenant never falls through to nearby Provozovny.
    if (isTenantDirectorySurface) {
      return null;
    }
    if (hasDiscoveryQuery || offersForResultsView.length === 0) {
      return null;
    }
    if (!hasNearbyShellIntent && intentGroupSortMode !== 'distance') {
      return null;
    }
    const groups = groupDiscoveryOffersBySalesPoint(offersForResultsView, intentGroupSortMode);
    return groups.length > 0 ? groups : null;
  }, [
    hasDiscoveryQuery,
    hasNearbyShellIntent,
    intentGroupSortMode,
    isTenantDirectorySurface,
    offersForResultsView,
  ]);

  const browseResultsLayout = useMemo(():
    | 'flat-offers'
    | 'nearby-sp-directory'
    | 'tenant-directory' => {
    if (isTenantDirectorySurface) {
      return 'tenant-directory';
    }
    if (nearbySalesPointGroups !== null) {
      return 'nearby-sp-directory';
    }
    return 'flat-offers';
  }, [isTenantDirectorySurface, nearbySalesPointGroups]);

  const mapItems = useMemo(() => toMapItems(offersForResultsView), [offersForResultsView]);

  const discoveryFilterIdentity = useMemo(
    () => discoveryFilterIdentityKey(params),
    [params],
  );

  /** G14 — view (incl. favorites/bag) remount clears list scroll via filter identity. */
  useEffect(() => {
    if (prevDiscoveryFilterIdentityRef.current === discoveryFilterIdentity) {
      return;
    }
    prevDiscoveryFilterIdentityRef.current = discoveryFilterIdentity;
    clearDiscoveryListScroll();
  }, [discoveryFilterIdentity]);

  const favoritesFilterResolving =
    params.view === 'favorites' &&
    favoritesLoading &&
    favoriteSalesPointIds.size === 0;

  const {
    fetchNextPage: discoveryFetchNextPage,
    hasNextPage: discoveryHasNextPage,
    isError: discoveryIsError,
    isFetching: discoveryIsFetching,
    isFetchingNextPage: discoveryIsFetchingNextPage,
    isPending: discoveryIsPending,
  } = discoveryQuery;

  useEffect(() => {
    if (params.view !== 'map') {
      return;
    }
    // Recovery/error must gate independently of hasNextPage. After page-2 422
    // (before deferred resetQueries), page 1's nextCursor is still set
    // (hasNextPage true). Unique-window proof is twin tests, not comments alone:
    // ungated spy bypass → staleCursorCalls === 2; gated path → === 1.
    // Read recovery ref inside this effect (after cursor-recovery effect runs).
    const blockedByCursorRecovery = isMapAutoPaginateBlockedByCursorRecovery({
      isError: discoveryIsError,
      isFetching: discoveryIsFetching,
      recoveryAttemptedForCurrentKey:
        cursorRecoveryAttemptedKeyRef.current === recoveryKey,
    });
    if (blockedByCursorRecovery) {
      return;
    }
    if (!discoveryHasNextPage || discoveryIsFetchingNextPage) {
      return;
    }
    void discoveryFetchNextPage();
  }, [
    params.view,
    discoveryFetchNextPage,
    discoveryHasNextPage,
    discoveryIsError,
    discoveryIsFetching,
    discoveryIsFetchingNextPage,
    discoveryIsPending,
    recoveryKey,
  ]);

  const distanceFormatter = useMemo<FormatDistanceMetersLabels>(
    () => ({
      meters: t('directory.distance.meters', { value: '{{value}}' }),
      kilometers: t('directory.distance.kilometers', { value: '{{value}}' }),
    }),
    [t],
  );

  const handleOpenOffer = useCallback((offer: PublicDiscoveryOfferItemDTO): void => {
    emitDiscoveryOfferSelection(offer, 'shop');
    const explicitUrl =
      typeof offer.salesPointContext.customerShopUrl === 'string'
        ? offer.salesPointContext.customerShopUrl.trim()
        : '';
    const fallbackUrl =
      explicitUrl.length > 0
        ? explicitUrl
        : `/${encodeURIComponent(offer.tenantContext.tenantCode)}/${encodeURIComponent(offer.salesPointContext.code)}`;
    const action = resolveDirectoryShopOpenAction({
      customerShopUrl: fallbackUrl,
      targetTenantCode: offer.tenantContext.tenantCode,
    });
    if (action !== null && action.kind === 'navigate') {
      navigate(action.url);
    }
  }, [emitDiscoveryOfferSelection, navigate]);

  /**
   * G7 — enforce a single overlay: close filters / taxonomy / map offer-picker / detail as needed.
   * Opening Filters / All-categories also leaves map view (default).
   * List|Map toggle and pin select pass `preserveMapView` so map stays when intended;
   * pin select additionally preserves the selection it is about to set.
   */
  const dismissConflictingOverlays = useCallback(
    (options?: {
      readonly preserveMapView?: boolean;
      readonly preserveMapSelection?: boolean;
      readonly preserveDetail?: boolean;
      readonly preserveRankingDisclosure?: boolean;
    }): void => {
      if (options?.preserveMapSelection !== true) {
        setSelectedSalesPointId(null);
        setMapOfferPickerSalesPointId(null);
      }
      if (options?.preserveDetail !== true) {
        setDetailOffer(null);
      }
      setFiltersOpen(false);
      setCategoryPickerOpen(false);
      if (options?.preserveRankingDisclosure !== true) {
        setRankingDisclosureOpen(false);
      }
      if (options?.preserveMapView !== true && params.view === 'map') {
        setView('list');
      }
    },
    [params.view, setView],
  );

  const applyDiscoveryAddResult = (
    offer: PublicDiscoveryOfferItemDTO,
    result: ReturnType<typeof addDiscoveryOfferToCart>,
    reviewRisk: string | null,
  ): boolean => {
    if (result.ok) {
      showToast({
        message: t('browse.discovery.addedToCartToast'),
        variant: 'success',
      });
      return true;
    }
    if (result.reason === 'price_unavailable') {
      showToast({
        message: t('browse.discovery.addUnavailableToast'),
        variant: 'info',
      });
      return false;
    }
    if (result.reason === 'storage_failed') {
      showToast({
        message: t('browse.discovery.cartSaveFailedToast'),
        variant: 'danger',
      });
      return false;
    }
    if (result.reason === 'age_gate_unresolved') {
      showToast({
        message: t('morChrome.ageGate.pending'),
        variant: 'info',
      });
      return false;
    }
    if (result.reason === 'age_gate_required') {
      setAgeGateDialogTenant(offer.tenantContext.tenantCode.trim());
      setAgeGateDialogReviewRisk(reviewRisk);
      setPendingAgeGateOffer(offer);
      showToast({
        message: t('morChrome.ageGate.requiredToast'),
        variant: 'info',
      });
      return false;
    }
    showToast({
      message: t('shared.unexpectedError'),
      variant: 'danger',
    });
    return false;
  };

  /**
   * GoPay MoR G5/G2 — settle seller-legal then certifyAgeGate inside addDiscoveryOfferToCart.
   * Success cache hit → sync add. Pending OR error → fail-closed (never treat error as resolved /
   * N/A-allow); async fetch may resolve or stay blocked.
   */
  const handleAddOffer = (offer: PublicDiscoveryOfferItemDTO): boolean => {
    emitDiscoveryOfferSelection(offer, 'add');
    const tenantCode = offer.tenantContext.tenantCode.trim();
    const queryState = queryClient.getQueryState(customerQueryKeys.sellerLegal(tenantCode));
    // G2: isSuccess only — isError must not N/A-allow with null reviewRisk.
    if (isSellerLegalCacheSuccessForAgeGate(queryState?.status)) {
      const cached = queryClient.getQueryData<{ reviewRisk?: string | null } | null>(
        customerQueryKeys.sellerLegal(tenantCode),
      );
      const reviewRisk = cached?.reviewRisk ?? null;
      const result = addDiscoveryOfferToCart(offer, {
        sellerLegalResolved: true,
        reviewRisk,
      });
      return applyDiscoveryAddResult(offer, result, reviewRisk);
    }

    showToast({
      message: t('morChrome.ageGate.pending'),
      variant: 'info',
    });
    void fetchSellerLegalAgeGateSnapshot(queryClient, api, tenantCode).then((snap) => {
      if (!snap.resolved) {
        showToast({
          message: t('morChrome.ageGate.pending'),
          variant: 'info',
        });
        return;
      }
      const result = addDiscoveryOfferToCart(offer, {
        sellerLegalResolved: true,
        reviewRisk: snap.reviewRisk,
      });
      const ok = applyDiscoveryAddResult(offer, result, snap.reviewRisk);
      if (ok) {
        setDetailOffer((current) =>
          current !== null &&
          current.productContext.productId === offer.productContext.productId &&
          current.salesPointContext.salesPointId === offer.salesPointContext.salesPointId
            ? null
            : current,
        );
      }
    });
    return false;
  };

  /** Detail sheet: close only after successful add; keep toast via handleAddOffer. */
  const handleAddOfferFromDetail = (offer: PublicDiscoveryOfferItemDTO): void => {
    if (handleAddOffer(offer)) {
      setDetailOffer(null);
    }
  };

  const handleAgeGateAcknowledged = (): void => {
    const pending = pendingAgeGateOffer;
    setPendingAgeGateOffer(null);
    setAgeGateDialogTenant(null);
    if (pending === null) {
      return;
    }
    const reviewRisk = ageGateDialogReviewRisk;
    const result = addDiscoveryOfferToCart(pending, {
      sellerLegalResolved: true,
      reviewRisk,
    });
    const ok = applyDiscoveryAddResult(pending, result, reviewRisk);
    if (ok) {
      setDetailOffer((current) =>
        current !== null &&
        current.productContext.productId === pending.productContext.productId &&
        current.salesPointContext.salesPointId === pending.salesPointContext.salesPointId
          ? null
          : current,
      );
    }
  };

  const handleOpenDetail = (
    offer: PublicDiscoveryOfferItemDTO,
    fromGrouped = false,
  ): void => {
    emitDiscoveryOfferSelection(offer, 'open');
    dismissConflictingOverlays({ preserveDetail: true });
    setDetailFromGrouped(fromGrouped);
    setDetailOffer(offer);
  };

  /**
   * G8 — one pin per SP; single-offer navigates immediately via customerShopUrl handoff;
   * multi-offer opens an explicit picker before navigate.
   * G7 — pin / offer-picker select dismisses filters + category sheets first (single overlay).
   */
  const handleSelectSalesPointId = (id: number | null): void => {
    dismissConflictingOverlays({
      preserveMapView: true,
      preserveMapSelection: true,
    });
    if (id === null) {
      setSelectedSalesPointId(null);
      setMapOfferPickerSalesPointId(null);
      return;
    }
    const spOffers = offersForSalesPoint(offersForResultsView, id);
    if (spOffers.length <= 1) {
      setMapOfferPickerSalesPointId(null);
      setSelectedSalesPointId(null);
      const only = spOffers[0];
      if (only !== undefined) {
        handleOpenOffer(only);
      }
      return;
    }
    setSelectedSalesPointId(id);
    setMapOfferPickerSalesPointId(id);
  };

  const mapPickerOffers =
    mapOfferPickerSalesPointId === null
      ? []
      : offersForSalesPoint(offersForResultsView, mapOfferPickerSalesPointId);

  const handleMapViewportChange = useCallback((next: ShopsDirectoryMapViewportState): void => {
    setMapViewport((prev) => {
      if (prev !== null && shopsDirectoryMapViewportEqual(prev, next)) {
        return prev;
      }
      return next;
    });
    writeDiscoveryMapViewport(next);
  }, []);

  const requestNearby = useCallback((): void => {
    setGeoPending(true);
    setGeoErrorKey(null);
    setManualLocationLabel(null);
    acquireBrowserPosition(
      (position) => {
        setGeoPending(false);
        setGeoErrorKey(null);
        setLocationSource('gps');
        setLocation(position);
        // Single URL write so sort cannot clobber radius (sequential replaceParams race).
        replaceParams({
          radiusMeters: params.radiusMeters ?? DEFAULT_RADIUS_METERS,
          sort: 'distance',
        });
      },
      (denied) => {
        setGeoPending(false);
        setGeoErrorKey(denied ? 'denied' : 'unavailable');
      },
    );
  }, [params.radiusMeters, replaceParams]);

  const applyManualPlace = useCallback(
    (place: DiscoveryManualPlaceSelection): void => {
      setGeoPending(false);
      setGeoErrorKey(null);
      setLocationSource('manual');
      setManualLocationLabel(place.label);
      setLocation({ lat: place.lat, lng: place.lng });
      replaceParams({
        radiusMeters: params.radiusMeters ?? DEFAULT_RADIUS_METERS,
        sort: 'distance',
      });
    },
    [params.radiusMeters, replaceParams],
  );

  const clearNearbyContext = useCallback((): void => {
    setLocation(null);
    setLocationSource(null);
    setManualLocationLabel(null);
    setGeoErrorKey(null);
    clearDiscoveryNearbyContext();
    replaceParams({
      radiusMeters: null,
      sort: 'relevance',
    });
  }, [replaceParams]);

  const discoveryShellLocationApi = useMemo(
    () => ({
      nearbyActive: hasNearbyShellIntent,
      geoPending,
      geoErrorKey,
      radiusMeters: params.radiusMeters,
      locationLabel: userLocationLabel,
      requestNearby,
      applyManualPlace,
      clearNearbyContext,
    }),
    [
      applyManualPlace,
      clearNearbyContext,
      geoErrorKey,
      geoPending,
      hasNearbyShellIntent,
      params.radiusMeters,
      requestNearby,
      userLocationLabel,
    ],
  );

  useRegisterDiscoveryShellLocation(discoveryShellLocationApi);

  /** Product path: URL filter reset always clears in-memory + session nearby together. */
  const resetFiltersAndNearby = (options?: {
    readonly clearScroll?: boolean;
  }): void => {
    clearFilters();
    clearNearbyContext();
    if (options?.clearScroll === true) {
      clearDiscoveryListScroll();
    }
  };

  const emptyStateKind = resolveBrowseEmptyStateKind({
    favoritesView: params.view === 'favorites',
    offersEmpty: offers.length === 0,
    hasLocationContext,
    radiusMeters: params.radiusMeters,
  });
  // A19 — same ONLINE_ONLY gate as ShopsDirectoryMap (plottable nearby count + directory source).
  const showNearbyShippingOnlyOmitNote = shouldShowBrowseNearbyShippingOnlyOmitNote({
    emptyStateKind,
    plottableCount: offersForResultsView.length,
    items: shopDirectory.items,
  });
  const showDiscoveryResultsSkeleton =
    !isTenantDirectorySurface &&
    (discoveryQuery.isPending || favoritesFilterResolving);
  /** Empty when the active results view has no rows (flat / entity-filtered / intent-scoped). */
  const resultsViewEmpty = offersForResultsView.length === 0;
  const showResultsEmpty =
    !isTenantDirectorySurface &&
    !showDiscoveryResultsSkeleton &&
    !discoveryQuery.isError &&
    !isNeutralDiscoverySurface &&
    params.view === 'list' &&
    resultsViewEmpty;
  const hasLocationFacets = hasLocationContext;
  const locationFacetsActive =
    hasLocationFacets &&
    (params.sort !== 'relevance' || params.radiusMeters !== null);

  const renderListResults = (): JSX.Element | null => {
    if (params.view !== 'list') {
      return null;
    }

    const mode = resolveBrowseListResultsMode({
      view: params.view,
      q: params.q,
      entity: params.entity,
      listKind: params.listKind,
      hasNearbySalesPointGroups: nearbySalesPointGroups !== null,
    });

    if (mode === 'tenant_directory') {
      return (
        <BrowseTenantDirectoryResults
          query={params.q}
          showHeading={params.entity === 'tenant' || params.listKind === 'tenant'}
        />
      );
    }

    if (mode === 'shop_directory_paginated' || mode === 'shop_directory_filtered') {
      return (
        <BrowseShopDirectoryResults
          items={shopDirectory.items}
          isPending={shopDirectory.isPending}
          isError={shopDirectory.isError}
          onRetry={() => {
            shopDirectory.refetch();
          }}
          hasNextPage={shopDirectory.hasNextPage}
          isFetchingNextPage={shopDirectory.isFetchingNextPage}
          onLoadMore={() => {
            shopDirectory.fetchNextPage();
          }}
        />
      );
    }

    if (mode === 'vse_three_sections') {
      const productsSection =
        offersForResultsView.length === 0 ? (
          <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
            {t('browse.discovery.emptyBody')}
          </p>
        ) : (
          <DirectoryResults
            density="list"
            listLayout="split2"
            testId="customer-browse-vse-product-results"
            aria-live="polite"
          >
            {offersForResultsView.map((offer) => (
              <DiscoveryOfferRow
                key={`${offer.salesPointContext.salesPointId}:${offer.productContext.productId}:${offer.productContext.variantId ?? 'base'}`}
                offer={offer}
                formatPrice={formatPrice}
                distanceFormatter={distanceFormatter}
                onAdd={handleAddOffer}
                onDetail={(o) => {
                  handleOpenDetail(o, true);
                }}
                onGoToShop={handleOpenOffer}
              />
            ))}
          </DirectoryResults>
        );

      return (
        <BrowseVseSearchSections
          productCount={offersForResultsView.length}
          productsSection={productsSection}
          shops={shopDirectory.items}
          shopsPending={shopDirectory.isPending}
          shopsError={shopDirectory.isError}
          onShopsRetry={() => {
            shopDirectory.refetch();
          }}
          tenants={tenantDirectory.items}
          tenantsPending={tenantDirectory.isPending}
          tenantsError={tenantDirectory.isError}
          onTenantsRetry={() => {
            tenantDirectory.refetch();
          }}
          query={params.q}
        />
      );
    }

    if (mode === 'product_empty_guidance') {
      return (
        <Card padded data-testid="customer-browse-product-empty-guidance">
          <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
            {t('browse.discovery.discoveryLandingBody')}
          </p>
        </Card>
      );
    }

    if (
      mode === 'product_offers' &&
      (showDiscoveryResultsSkeleton || discoveryQuery.isError)
    ) {
      return null;
    }

    if (mode === 'nearby_sales_points' && nearbySalesPointGroups !== null) {
      // Heading + Zobrazit vše owned by mode-chrome band (W5 LEFT title / RIGHT link).
      return (
        <div className="space-y-3" data-testid="customer-browse-nearby-sp-section">
          <BrowseNearbySalesPointResults
            groups={nearbySalesPointGroups}
            distanceFormatter={distanceFormatter}
          />
        </div>
      );
    }

    if (offersForResultsView.length === 0) {
      return null;
    }

    return (
      <DirectoryResults
        density="list"
        listLayout="split2"
        testId="customer-browse-results"
        aria-live="polite"
      >
        {offersForResultsView.map((offer) => (
          <DiscoveryOfferRow
            key={`${offer.salesPointContext.salesPointId}:${offer.productContext.productId}:${offer.productContext.variantId ?? 'base'}`}
            offer={offer}
            distanceFormatter={distanceFormatter}
            formatPrice={formatPrice}
            onAdd={handleAddOffer}
            onDetail={handleOpenDetail}
            onGoToShop={handleOpenOffer}
          />
        ))}
      </DirectoryResults>
    );
  };

  /** G8 — ☆ universe from personal favorites (contacts/pending), not discovery intersection. */
  const favoriteSalesPointGroups = useMemo((): readonly DiscoveryIntentSalesPointGroup[] => {
    if (params.view !== 'favorites') {
      return [];
    }
    if (favoritesSignedIn) {
      return mapSignedInFavoriteContactsToSalesPointGroups(signedInFavoriteContacts);
    }
    return mapPendingFavoriteContactsToSalesPointGroups(guestPendingFavorites);
  }, [
    favoritesSignedIn,
    guestPendingFavorites,
    params.view,
    signedInFavoriteContacts,
  ]);

  const categoryFallbackLabel = t('browse.discovery.categoryFallback');
  const categoryLabelLocale = i18n.resolvedLanguage ?? i18n.language ?? 'cs';

  /** G4 — under-search clear chip label (home tiles or taxonomy tree; never raw slug). */
  const activeCategoryLabel = useMemo((): string => {
    if (params.platformCategory === null) {
      return '';
    }
    const key = params.platformCategory;
    const fromHome = visibleHomeCategories.find(
      (category) => category.slug === key || String(category.id) === key,
    );
    if (fromHome !== undefined) {
      return categoryLabel(fromHome, categoryLabelLocale, categoryFallbackLabel);
    }
    const fromTree = flattenCategoryTree(categoriesQuery.data?.items ?? []).find(
      ({ category }) => category.slug === key || String(category.id) === key,
    );
    if (fromTree !== undefined) {
      return categoryLabel(fromTree.category, categoryLabelLocale, categoryFallbackLabel);
    }
    return categoryFallbackLabel;
  }, [
    categoriesQuery.data?.items,
    categoryFallbackLabel,
    categoryLabelLocale,
    params.platformCategory,
    visibleHomeCategories,
  ]);

  /**
   * W5 / R2 — Zobrazit vše = section heading RIGHT link (Obchody landing row deleted).
   * Atomic push: clear nearby narrowing + entity=shop (FR-11). Sequential clearNearby +
   * setEntity races on locationRef and can leave radius= in the URL.
   * Shop engagement after Obchody delete: entity=shop via this link or BrowseEntityChips.
   */
  const handleShowAllSalesPointsDirectory = useCallback((): void => {
    dismissConflictingOverlays();
    setLocation(null);
    setLocationSource(null);
    setManualLocationLabel(null);
    setGeoErrorKey(null);
    clearDiscoveryNearbyContext();
    pushParams({
      entity: 'shop',
      radiusMeters: null,
      sort: 'relevance',
    });
  }, [dismissConflictingOverlays, pushParams]);

  const isDefaultSeznamScreen1 = homeVisibility.isDefaultSeznamProvozovny;
  // D22 / §3.2.14 — Tenanti / Mapa / ☆ / Taška★ collapse landing search (rail+promo via homeVisibility).
  const showBrowseSearchChrome = homeVisibility.showSearch;

  const trimmedUserLocationLabel =
    userLocationLabel !== null && userLocationLabel.trim().length > 0
      ? userLocationLabel.trim()
      : null;
  let locationPinLabel = t('browse.discovery.nearbyCta');
  if (geoPending && trimmedUserLocationLabel === null) {
    locationPinLabel = t('browse.discovery.locating');
  } else if (trimmedUserLocationLabel !== null) {
    locationPinLabel = trimmedUserLocationLabel;
  }

  const nearbySpHeadingText =
    nearbySalesPointGroups !== null
      ? t('browse.discovery.nearbySalesPointsHeading', {
          count: nearbySalesPointGroups.length,
          defaultValue: `Provozovny v okolí (${nearbySalesPointGroups.length})`,
        })
      : t('browse.discovery.nearbySalesPointsHeadingEmpty', {
          defaultValue: 'Provozovny v okolí',
        });

  let rankingDisclosureEntry: JSX.Element | null = null;
  if (isDefaultSeznamScreen1) {
    rankingDisclosureEntry = (
      <div
        className="flex w-full min-w-0 flex-col gap-1"
        data-testid="customer-browse-nearby-sp-heading-band"
      >
        <div
          className="flex w-full min-w-0 items-center justify-between gap-3"
          data-testid="customer-browse-nearby-sp-heading-row"
        >
          <h2
            id="customer-browse-nearby-sp-heading"
            className="min-w-0 flex-1 text-base font-semibold text-[var(--color-on-surface)] sm:text-lg"
            data-testid="customer-browse-nearby-sp-heading"
          >
            {nearbySpHeadingText}
          </h2>
          <button
            type="button"
            className={[
              'inline-flex shrink-0 items-center px-2 text-[length:var(--vcl-type-label-size)] font-[number:var(--vcl-type-label-weight)] text-[var(--color-accent)] underline',
              CUSTOMER_MIN_TOUCH_HEIGHT_CLASS,
              CUSTOMER_CONTROL_RADIUS_SM_CLASS,
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
              'focus-visible:outline-[var(--color-focus-ring)]',
            ].join(' ')}
            data-testid="customer-browse-show-all"
            onClick={handleShowAllSalesPointsDirectory}
          >
            {t('browse.discovery.showAll', { defaultValue: 'Zobrazit vše' })}
          </button>
        </div>
        {/* W7 / H08 / R9 — ranking disclosure near results heading. H09 LEGAL classification Blocked — no marketplace UI. */}
        <div
          className="flex w-full min-w-0"
          data-testid="customer-browse-ranking-disclosure-entry"
        >
          <button
            type="button"
            className={[
              'inline-flex w-fit max-w-full items-center px-0.5 text-left text-[length:var(--vcl-type-label-size)] text-[var(--color-on-surface-muted)] underline',
              CUSTOMER_MIN_TOUCH_HEIGHT_CLASS,
              CUSTOMER_CONTROL_RADIUS_SM_CLASS,
              'hover:text-[var(--color-accent)]',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
              'focus-visible:outline-[var(--color-focus-ring)]',
            ].join(' ')}
            data-testid="customer-browse-ranking-disclosure-open"
            onClick={() => {
              dismissConflictingOverlays({
                preserveDetail: true,
                preserveRankingDisclosure: true,
              });
              setRankingDisclosureOpen(true);
            }}
          >
            {t('browse.discovery.rankingDisclosureTitle')}
          </button>
        </div>
      </div>
    );
  } else if (params.view === 'list') {
    rankingDisclosureEntry = (
      <div
        className="flex w-full min-w-0"
        data-testid="customer-browse-ranking-disclosure-entry"
      >
        {/* W7 / H08 / R9 — list sort context; H09 LEGAL classification Blocked. */}
        <button
          type="button"
          className={[
            'inline-flex w-fit max-w-full items-center px-0.5 text-left text-[length:var(--vcl-type-label-size)] text-[var(--color-on-surface-muted)] underline',
            CUSTOMER_MIN_TOUCH_HEIGHT_CLASS,
            CUSTOMER_CONTROL_RADIUS_SM_CLASS,
            'hover:text-[var(--color-accent)]',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
            'focus-visible:outline-[var(--color-focus-ring)]',
          ].join(' ')}
          data-testid="customer-browse-ranking-disclosure-open"
          onClick={() => {
            dismissConflictingOverlays({
              preserveDetail: true,
              preserveRankingDisclosure: true,
            });
            setRankingDisclosureOpen(true);
          }}
        >
          {t('browse.discovery.rankingDisclosureTitle')}
        </button>
      </div>
    );
  }

  return (
    <div
      className={[
        // BR-08 / C8: directory/shell width via DIRECTORY_PAGE_STACK_CLASS (full main pane —
        // not CUSTOMER_CONTENT_STANDARD). Soft-keyboard inset preserved below. Residual
        // hardcodes → docs/governance/evidence/vcl-step6-wave3/BR08_BROWSE.md (DEBT).
        DIRECTORY_PAGE_STACK_CLASS,
        'pb-[calc(var(--customer-bottom-chrome,calc(4.75rem+env(safe-area-inset-bottom,0px)))+var(--customer-keyboard-inset,0px))]',
      ].join(' ')}
      data-testid="customer-browse-placeholder"
      // §3.1 Exit — single discovery core in main pane (desktop sidebar is nav-only, not a second Home).
      data-discovery-core="single"
      data-discovery-view={params.view}
      data-list-kind={params.listKind}
      data-entity={params.entity}
      data-nearby-active={hasNearbyShellIntent ? '1' : '0'}
      data-search-intent-active={hasDiscoveryQuery ? '1' : '0'}
      data-results-layout={browseResultsLayout}
      data-keyboard-inset-px={String(keyboardInsetPx)}
      data-list-scroll-restored={
        appliedListScrollRestoreY === null ? undefined : String(appliedListScrollRestoreY)
      }
      style={{
        // Keep search/results above soft keyboard without collapsing bottom nav clearance.
        scrollPaddingBottom: `calc(0.75rem + var(--customer-keyboard-inset, 0px))`,
      }}
    >
      {/* HUX-14 / D11 — a11y page name only; no visible Org/Back/Nakupovat row above search. */}
      <h1 className="sr-only" data-testid="customer-browse-page-title">
        {t('nav.browse')}
      </h1>
      {/*
        D12 / HUX-02 — location-first single compact row (pin + coarse label + chevron).
        Discovery location only (≠ shipping). GPS is opt-in via disclosure — never on mount.
      */}
      <div
        className={[
          'flex w-full max-w-full items-center gap-[var(--vcl-gap-inline-sm)]',
          CUSTOMER_MIN_TOUCH_HEIGHT_CLASS,
          hasNearbyShellIntent
            ? `${CUSTOMER_CONTROL_RADIUS_SM_CLASS} border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-2.5 py-1`
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid="customer-browse-nearby-section"
        data-discovery-location="1"
      >
        <button
          type="button"
          className={[
            'inline-flex min-w-0 flex-1 items-center gap-[var(--vcl-gap-inline-sm)] text-left text-[length:var(--vcl-type-body-sm-size)] font-[number:var(--vcl-type-h4-weight)] text-[var(--color-on-surface)]',
            CUSTOMER_MIN_TOUCH_HEIGHT_CLASS,
            CUSTOMER_CONTROL_RADIUS_SM_CLASS,
            'hover:text-[var(--color-accent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
            'focus-visible:outline-[var(--color-focus-ring)]',
          ].join(' ')}
          onClick={openLocationDisclosure}
          aria-label={trimmedUserLocationLabel ?? t('browse.discovery.nearbyCta')}
          data-testid="customer-browse-nearby-open"
        >
          <Icon icon={MapPin} size={20} aria-hidden={true} className="shrink-0" />
          <span
            className="min-w-0 truncate"
            data-testid="customer-browse-location-label"
          >
            {locationPinLabel}
          </span>
          <Icon
            icon={ChevronDown}
            size={16}
            aria-hidden={true}
            className="shrink-0 opacity-70"
          />
        </button>
        {hasNearbyShellIntent ? (
          <div
            className="shrink-0 text-xs leading-snug"
            data-testid="customer-browse-location-detail"
          >
            <button
              type="button"
              className={`inline-flex items-center font-medium text-[var(--color-accent)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] ${CUSTOMER_MIN_TOUCH_HEIGHT_CLASS}`}
              data-testid="customer-browse-location-change"
              onClick={openLocationDisclosure}
            >
              {t('browse.discovery.changeLocation')}
            </button>
          </div>
        ) : null}
      </div>

      <div className="space-y-3" data-testid="customer-browse-results-context">
        {geoErrorKey !== null ? (
          <Card padded data-testid={`customer-browse-geo-${geoErrorKey}`}>
            <div className="space-y-3">
              <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface)]">
                {t(
                  geoErrorKey === 'denied'
                    ? 'browse.discovery.locationDenied'
                    : 'browse.discovery.locationUnavailable',
                )}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  intent="secondary"
                  size="md"
                  onClick={openLocationDisclosure}
                  data-testid="customer-browse-geo-retry"
                >
                  {t('shared.retry')}
                </Button>
                <Button
                  type="button"
                  intent="ghost"
                  size="md"
                  onClick={clearNearbyContext}
                  data-testid="customer-browse-geo-clear-nearby"
                >
                  {t('browse.discovery.resetFilters')}
                </Button>
              </div>
            </div>
          </Card>
        ) : null}
      </div>

      {showBrowseSearchChrome ? (
        <div
          className="shrink-0"
          data-testid="customer-browse-search-chrome"
          style={
            keyboardInsetPx > 0
              ? { paddingBottom: `min(${keyboardInsetPx}px, 40vh)` }
              : undefined
          }
        >
          <DirectorySearchField
            draft={draftQuery}
            onDraftChange={setDraftOverride}
            onCommit={(value) => {
              setDraftOverride(null);
              setQ(value);
            }}
            onClear={() => {
              setDraftOverride(null);
              setQ('');
            }}
            placeholder={t('browse.discovery.searchPlaceholder')}
            ariaLabel={t('browse.discovery.searchAria')}
            clearAriaLabel={t('browse.discovery.clearSearchAria')}
            testIdPrefix="customer-browse-search"
            // D13 — full-width dominant 44–48px search; Filtry omitted on idle (below).
            emphasis="dominant"
            className="scroll-mt-[calc(env(safe-area-inset-top,0px)+3.5rem)]"
            {...(isNeutralDiscoverySurface
              ? {}
              : {
                  filterTrigger: {
                    onClick: () => {
                      dismissConflictingOverlays();
                      setFiltersOpen(true);
                    },
                    ariaLabel: locationFacetsActive
                      ? t('browse.discovery.openFiltersActive')
                      : t('browse.discovery.openFilters'),
                    testId: 'customer-browse-filters',
                    expanded: filtersOpen,
                    active: locationFacetsActive,
                  },
                })}
          />
          {homeVisibility.showEntityChips ? (
            <div className="mt-2">
              <BrowseEntityChips
                entity={params.entity}
                onEntityChange={(entity) => {
                  dismissConflictingOverlays();
                  setEntity(entity);
                }}
                allLabel={t('browse.discovery.entityChipAll')}
                productLabel={t('browse.discovery.entityChipProduct')}
                shopLabel={t('browse.discovery.entityChipShop')}
                tenantLabel={t('browse.discovery.entityChipTenant')}
                groupAriaLabel={t('browse.discovery.entityChipsAria')}
              />
            </div>
          ) : null}
          {/* G11 — single clear SSOT under search; categories header clear removed */}
          {hasCategoryFilter ? (
            <BrowseActiveCategoryChip
              label={activeCategoryLabel}
              onClear={clearCategory}
            />
          ) : null}
        </div>
      ) : null}

      {showHomeCategoriesSurface ? (
        <section aria-labelledby="customer-browse-categories-heading" className="space-y-3">
          {/* HUX-04: heading only — landing “Všechny kategorie” CTA removed; Další owns taxonomy. */}
          <h2
            id="customer-browse-categories-heading"
            className="text-[length:var(--vcl-type-h3-size)] font-[number:var(--vcl-type-h3-weight)] text-[var(--color-on-surface)]"
          >
            {t('browse.discovery.categoriesTitle')}
          </h2>

          {renderHomeCategoriesArea()}
        </section>
      ) : null}

      {/* D21/D22 — parent owns promo mount; incomplete payload still collapses inside banner (FR-14). */}
      {showHomePromoSurface ? (
        <BrowseHomePromoBanner
          title={homePromoActionable ? homePromoTitle : null}
          subtitle={homePromoBanner?.subtitle}
          ctaLabel={homePromoActionable ? homePromoCtaLabel : null}
          imageUrl={homePromoActionable ? homePromoImageUrl : null}
          targetType={homePromoActionable ? homePromoBanner?.targetType : null}
          targetRef={homePromoActionable ? homePromoBanner?.targetRef : null}
          onCtaClick={homePromoActionable ? handleHomePromoCta : undefined}
        />
      ) : null}

      {/* §3.2.14 — search-active (state 2): hide mode strip (Visible = location + search + chips + results). */}
      {homeVisibility.showDiscoveryControl ? (
        <div className="space-y-2" data-testid="customer-browse-mode-chrome">
          {/* W5/R2 Picture3: [ Seznam ─ Provozovny|Tenanti ] [ Mapa ] [ ☆ ] [ Taška★ ]; Obchody row deleted. */}
          <BrowseDiscoveryModeStrip
            view={params.view}
            listKind={params.listKind}
            onViewChange={(nextView) => {
              dismissConflictingOverlays({ preserveMapView: true });
              setView(nextView);
            }}
            onListKindChange={(nextKind) => {
              dismissConflictingOverlays();
              setListKind(nextKind);
            }}
          />
        </div>
      ) : null}

      {/* W5 — section heading LEFT title / RIGHT Zobrazit vše; shop via entity=shop (FR-11). */}
      {rankingDisclosureEntry}

      {showDiscoveryResultsSkeleton ? (
        <DirectoryResultsSkeleton
          density="list"
          count={4}
          // Home nearby / default Seznam → inset compact (HUX-09); search offer lists keep split2.
          size={homeVisibility.useCompactNearbyDensity ? 'md' : 'lg'}
          rowDensity={
            homeVisibility.useCompactNearbyDensity ? 'compact' : 'comfortable'
          }
          listLayout={homeVisibility.useCompactNearbyDensity ? 'inset' : 'split2'}
          testId="customer-browse-results-skeleton"
        />
      ) : null}

      {!showDiscoveryResultsSkeleton &&
      !isTenantDirectorySurface &&
      discoveryQuery.isError ? (
        <Card padded data-testid="customer-browse-error">
          <div className="space-y-2">
            <h2 className="text-[length:var(--vcl-type-h3-size)] font-[number:var(--vcl-type-h3-weight)] text-[var(--color-on-surface)]">
              {t('browse.discovery.errorTitle')}
            </h2>
            <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
              {typeof navigator !== 'undefined' && navigator.onLine === false
                ? t('browse.discovery.offlineBody')
                : t('browse.discovery.errorBody')}
            </p>
            {/* Match empty-state CTA row so recovery is not a stacked dead-end wall (§4). */}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                intent="primary"
                size="md"
                onClick={() => {
                  void discoveryQuery.refetch();
                }}
                data-testid="customer-browse-retry"
              >
                {t('shared.retry')}
              </Button>
              <Button
                type="button"
                intent="ghost"
                size="md"
                onClick={() => {
                  resetFiltersAndNearby({ clearScroll: true });
                }}
                data-testid="customer-browse-error-reset-filters"
              >
                {t('browse.discovery.resetFilters')}
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {showResultsEmpty ? (
        <Card padded data-testid="customer-browse-empty">
          <div className="space-y-2">
            <h2 className="text-[length:var(--vcl-type-h3-size)] font-[number:var(--vcl-type-h3-weight)] text-[var(--color-on-surface)]">
              {t(browseEmptyStateTitleKey(emptyStateKind))}
            </h2>
            <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
              {t(browseEmptyStateBodyKey(emptyStateKind))}
            </p>
            {showNearbyShippingOnlyOmitNote ? (
              <p
                className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]"
                data-testid="customer-browse-nearby-shipping-only-omit"
              >
                {t('customerPresence.nearbyOmitsShippingOnly')}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {emptyStateKind === 'favorites' ? (
                <Button
                  type="button"
                  intent="primary"
                  size="md"
                  onClick={() => {
                    setView('list');
                  }}
                  data-testid="customer-browse-favorites-show-all"
                >
                  {t('browse.discovery.showAll')}
                </Button>
              ) : (
                <Button
                  type="button"
                  intent="secondary"
                  size="md"
                  onClick={() => {
                    resetFiltersAndNearby({ clearScroll: true });
                  }}
                  data-testid="customer-browse-empty-reset"
                >
                  {t('browse.discovery.resetFilters')}
                </Button>
              )}
              {emptyStateKind === 'default' ? (
                <Button
                  type="button"
                  intent="ghost"
                  size="md"
                  onClick={openLocationDisclosure}
                  data-testid="customer-browse-empty-nearby"
                >
                  {t('browse.discovery.nearbyCta')}
                </Button>
              ) : null}
              {emptyStateKind === 'nearby' ? (
                <Button
                  type="button"
                  intent="ghost"
                  size="md"
                  onClick={() => {
                    setRadiusMeters(
                      params.radiusMeters !== null ? Math.min(params.radiusMeters * 2, 50_000) : 10_000,
                    );
                  }}
                  data-testid="customer-browse-empty-expand-radius"
                >
                  {t('browse.discovery.expandRadius')}
                </Button>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}

      {renderListResults()}

      {params.view === 'favorites' ? (
        <section
          className="space-y-3"
          data-testid="customer-browse-favorites-mode"
          aria-labelledby="customer-browse-favorites-heading"
        >
          <h2
            id="customer-browse-favorites-heading"
            className="text-[length:var(--vcl-type-h3-size)] font-[number:var(--vcl-type-h3-weight)] text-[var(--color-on-surface)]"
            data-testid="customer-browse-favorites-heading"
          >
            {t('browse.discovery.favoritesModeTitle', {
              defaultValue: 'Oblíbené provozovny',
            })}
          </h2>
          {(() => {
            if (favoritesFilterResolving) {
              return (
                <DirectoryResultsSkeleton
                  density="list"
                  count={3}
                  size="md"
                  rowDensity="compact"
                  listLayout="inset"
                  testId="customer-browse-favorites-skeleton"
                />
              );
            }
            if (favoriteSalesPointGroups.length > 0) {
              return (
                <BrowseNearbySalesPointResults
                  groups={favoriteSalesPointGroups}
                  distanceFormatter={distanceFormatter}
                />
              );
            }
            return (
              <Card padded data-testid="customer-browse-favorites-empty">
                <h3 className="text-[length:var(--vcl-type-h3-size)] font-[number:var(--vcl-type-h3-weight)] text-[var(--color-on-surface)]">
                  {t('browse.discovery.favoritesEmptyTitle')}
                </h3>
                <p className="mt-1 text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
                  {t('browse.discovery.favoritesEmptyBody')}
                </p>
                <Button
                  type="button"
                  intent="primary"
                  size="md"
                  className="mt-3"
                  onClick={() => {
                    setView('list');
                  }}
                  data-testid="customer-browse-favorites-show-all"
                >
                  {t('browse.discovery.showAll')}
                </Button>
              </Card>
            );
          })()}
        </section>
      ) : null}

      {params.view === 'bag' ? (
        <section
          className="space-y-3"
          data-testid="customer-browse-bag-mode"
          aria-labelledby="customer-browse-bag-heading"
        >
          <h2
            id="customer-browse-bag-heading"
            className="text-[length:var(--vcl-type-h3-size)] font-[number:var(--vcl-type-h3-weight)] text-[var(--color-on-surface)]"
            data-testid="customer-browse-bag-heading"
          >
            {t('browse.discovery.bagModeTitle', {
              defaultValue: 'Taška★',
            })}
          </h2>
          <BrowseBagResults
            onOpenItem={(item) => {
              handleOpenDetail(mapOfferFavoriteToDiscoveryOffer(item));
            }}
          />
        </section>
      ) : null}

      {/* G7 / D22 — view=map always mounts max-map after location+control (empty OK). */}
      {!showDiscoveryResultsSkeleton && !discoveryQuery.isError && params.view === 'map' ? (
        <div
          className="flex min-h-0 w-full min-w-0 flex-1 flex-col"
          data-testid="customer-browse-map"
        >
          <ShopsDirectoryMap
            visibleItems={mapItems}
            userPosition={resolvedLocation}
            selectedSalesPointId={selectedSalesPointId}
            mapReady={!discoveryQuery.hasNextPage}
            onSelectSalesPointId={handleSelectSalesPointId}
            onRequestMyLocation={openLocationDisclosure}
            persistTenant="platform-browse"
            // §3.2.14 state 4 — selection-card capability ON for Mapa; card mounts when pin selected.
            showSelectionCard
            // G4/G9 — discovery owns camera; hide Views + lock framing overlays.
            disableShopsSessionFraming
            hideViewsChrome
            onBackToList={() => {
              setSelectedSalesPointId(null);
              setMapOfferPickerSalesPointId(null);
              setView('list');
            }}
            restoredViewport={mapViewport}
            onViewportChange={handleMapViewportChange}
          />
        </div>
      ) : null}

      {discoveryQuery.hasNextPage &&
      params.view === 'list' &&
      !isTenantDirectorySurface ? (
        <div className="pt-2">
          <Button
            type="button"
            intent="secondary"
            size="md"
            block
            onClick={() => {
              void discoveryQuery.fetchNextPage();
            }}
            disabled={discoveryQuery.isFetchingNextPage}
            data-testid="customer-browse-load-more"
          >
            {discoveryQuery.isFetchingNextPage
              ? t('shared.loading')
              : t('browse.discovery.loadMore')}
          </Button>
        </div>
      ) : null}

      <DirectoryFilterSheet
        open={categoryPickerOpen}
        onClose={() => {
          setCategoryPickerOpen(false);
        }}
        title={t('browse.discovery.allCategories')}
        titleId="customer-browse-all-categories-title"
        closeLabel={t('shared.cancel')}
        testIdPrefix="customer-browse-all-categories"
        facetsActive={false}
        onClearAll={clearCategory}
        clearAllLabel={t('browse.discovery.resetFilters')}
        doneLabel={t('shared.cancel')}
      >
        <div className="space-y-2" data-testid="customer-browse-all-categories-list">
          <button
            type="button"
            className={[
              'w-full border px-3 py-3 text-left text-[length:var(--vcl-type-label-size)] font-[number:var(--vcl-type-label-weight)]',
              CUSTOMER_CONTROL_RADIUS_SM_CLASS,
              !hasCategoryFilter
                ? // HUX-12 — Další taxonomy active uses platform accent (rail residual wiring).
                  'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-accent-foreground)]'
                : 'border-[var(--color-border)] bg-[var(--color-surface-elevated)] text-[var(--color-on-surface)]',
            ].join(' ')}
            onClick={() => {
              clearCategory();
              setCategoryPickerOpen(false);
            }}
            aria-pressed={!hasCategoryFilter}
            data-testid="customer-browse-all-categories-option-all"
          >
            {t('browse.discovery.allCategories')}
          </button>
          {flattenCategoryTree(categoriesQuery.data?.items ?? []).map(({ category, depth }) => {
            const active =
              params.platformCategory === String(category.id) ||
              params.platformCategory === category.slug;
            return (
              <button
                key={`all-${category.id}`}
                type="button"
                className={[
                  'w-full border px-3 py-3 text-left text-[length:var(--vcl-type-label-size)] font-[number:var(--vcl-type-label-weight)]',
                  CUSTOMER_CONTROL_RADIUS_SM_CLASS,
                  active
                    ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-accent-foreground)]'
                    : 'border-[var(--color-border)] bg-[var(--color-surface-elevated)] text-[var(--color-on-surface)]',
                ].join(' ')}
                style={depth > 0 ? { paddingInlineStart: `${0.75 + depth * 0.75}rem` } : undefined}
                onClick={() => {
                  setPlatformCategory(category.slug);
                  setCategoryPickerOpen(false);
                }}
                aria-pressed={active}
                data-testid={`customer-browse-all-categories-option-${category.slug}`}
                data-category-depth={depth}
              >
                {categoryLabel(category, categoryLabelLocale, categoryFallbackLabel)}
              </button>
            );
          })}
        </div>
      </DirectoryFilterSheet>

      <DirectoryFilterSheet
        open={filtersOpen}
        onClose={() => {
          setFiltersOpen(false);
        }}
        title={t('browse.discovery.filtersTitle')}
        titleId="customer-browse-filters-title"
        closeLabel={t('shared.cancel')}
        testIdPrefix="customer-browse-filters"
        facetsActive={locationFacetsActive}
        onClearAll={() => {
          resetFiltersAndNearby();
        }}
        clearAllLabel={t('browse.discovery.resetFilters')}
        doneLabel={t('browse.discovery.applyFilters')}
      >
        <div className="space-y-4">
          <label className="flex flex-col gap-1 text-xs text-[var(--color-on-surface-muted)]">
            <span>{t('browse.discovery.sortLabel')}</span>
            <select
              className={`${CUSTOMER_MIN_TOUCH_HEIGHT_CLASS} ${CUSTOMER_CONTROL_RADIUS_SM_CLASS} border border-[var(--color-border-strong)] bg-[var(--color-surface-elevated)] px-3 py-2 text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface)]`}
              value={params.sort}
              onChange={(event) => {
                setSort(event.target.value === 'distance' ? 'distance' : 'relevance');
              }}
            >
              <option value="relevance">{t('browse.discovery.sort.relevance')}</option>
              {hasLocationFacets ? (
                <option value="distance">{t('browse.discovery.sort.distance')}</option>
              ) : null}
            </select>
          </label>
          {hasLocationFacets ? (
            <label className="flex flex-col gap-1 text-xs text-[var(--color-on-surface-muted)]">
              <span>{t('browse.discovery.radiusLabel')}</span>
              <select
                className={`${CUSTOMER_MIN_TOUCH_HEIGHT_CLASS} ${CUSTOMER_CONTROL_RADIUS_SM_CLASS} border border-[var(--color-border-strong)] bg-[var(--color-surface-elevated)] px-3 py-2 text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface)]`}
                value={String(params.radiusMeters ?? DEFAULT_RADIUS_METERS)}
                onChange={(event) => {
                  setRadiusMeters(Number.parseInt(event.target.value, 10));
                }}
              >
                {FILTER_RADIUS_OPTIONS.map((radius) => (
                  <option key={radius} value={radius}>
                    {t('browse.discovery.radiusOption', { km: radius / 1000 })}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </DirectoryFilterSheet>

      <DirectoryFilterSheet
        open={mapOfferPickerSalesPointId !== null}
        onClose={() => {
          setMapOfferPickerSalesPointId(null);
          setSelectedSalesPointId(null);
        }}
        title={t('browse.discovery.mapOfferPickerTitle')}
        titleId="customer-browse-map-offers-title"
        closeLabel={t('shared.cancel')}
        testIdPrefix="customer-browse-map-offers"
        facetsActive={false}
        onClearAll={() => {
          setMapOfferPickerSalesPointId(null);
          setSelectedSalesPointId(null);
        }}
        clearAllLabel={t('shared.cancel')}
        doneLabel={t('shared.cancel')}
      >
        <p
          className="mb-3 text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]"
          data-testid="customer-browse-map-offers-hint"
        >
          {t('browse.discovery.mapOfferPickerHint')}
        </p>
        <div className="space-y-2" data-testid="customer-browse-map-offers-list">
          {mapPickerOffers.map((offer) => {
            const identity = offerRowIdentity(offer);
            return (
              <button
                key={`${offer.salesPointContext.salesPointId}:${identity}`}
                type="button"
                className={`flex w-full items-center justify-between gap-[var(--vcl-gap-inline-md)] border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3 py-3 text-left text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface)] ${CUSTOMER_CONTROL_RADIUS_SM_CLASS}`}
                onClick={() => {
                  setMapOfferPickerSalesPointId(null);
                  setSelectedSalesPointId(null);
                  handleOpenOffer(offer);
                }}
                data-testid={`customer-browse-map-offer-${identity}`}
              >
                <span className="min-w-0 flex-1 line-clamp-2 break-words font-medium">
                  {offer.title}
                </span>
                {offer.price !== null ? (
                  <span className="shrink-0 text-[length:var(--vcl-type-body-sm-size)] font-semibold tabular-nums">
                    {formatPrice(offer.price)}
                  </span>
                ) : (
                  <span className="shrink-0 text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
                    {t('browse.discovery.priceUnavailable')}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </DirectoryFilterSheet>

      <DiscoveryOfferDetailSheet
        open={detailOffer !== null}
        offer={detailOffer}
        detailContext={detailFromGrouped ? 'grouped' : 'flat'}
        formatPrice={formatPrice}
        distanceFormatter={distanceFormatter}
        onClose={() => {
          setDetailOffer(null);
          setDetailFromGrouped(false);
        }}
        onAdd={handleAddOfferFromDetail}
        onGoToShop={(offer) => {
          handleOpenOffer(offer);
          setDetailOffer(null);
        }}
      />

      {ageGateDialogTenant !== null && pendingAgeGateOffer !== null ? (
        <AgeGateDialog
          tenantCode={ageGateDialogTenant}
          products={[{ ageRestricted: pendingAgeGateOffer.ageRestricted === true }]}
          reviewRisk={ageGateDialogReviewRisk}
          inputsResolved
          testIdPrefix="customer-age-gate-browse"
          onAcknowledged={handleAgeGateAcknowledged}
        />
      ) : null}

      {/* H08 — mode-aware shell from effective list sort (relevance|distance). Promo shell unused until paid ranking exists — BAN LEGAL invent. */}
      <BrowseRankingDisclosureSheet
        open={rankingDisclosureOpen}
        onClose={() => {
          setRankingDisclosureOpen(false);
        }}
        sortMode={intentGroupSortMode}
      />
    </div>
  );
}
